import { CONSTANTS } from '#app/constants/index.ts'
import { useOptionalRequestInfo } from './request-info.ts'

/**
 * The origins of the three Spacebrains web presences.
 *
 * Resolved per request on the server (see `hosts.server.ts`) and passed to the
 * client through the root loader's `requestInfo`.
 */
export type HostOrigins = {
	marketing: string
	app: string
	agency: string
}

const FALLBACK_ORIGINS: HostOrigins = {
	marketing: CONSTANTS.MARKETING_ORIGIN,
	app: CONSTANTS.APP_ORIGIN,
	agency: CONSTANTS.AGENCY_ORIGIN,
}

/**
 * The other Spacebrains origins, as resolved by the root loader. Falls back to
 * the production origins inside error boundaries, where root loader data can be
 * missing.
 */
export function useHostOrigins(): HostOrigins {
	return useOptionalRequestInfo()?.hosts ?? FALLBACK_ORIGINS
}

/**
 * Resolves a path on one of the Spacebrains sites to the shortest href that
 * works from the current one.
 *
 * When the target site *is* the current origin the path is returned as-is, so
 * React Router can client-navigate. Otherwise it becomes an absolute URL -
 * which is not merely cosmetic: the hosts deliberately do not share session
 * cookies, so a cross-host link must be a real navigation.
 */
export function useSiteHref(origin: string, path = '/'): string {
	const currentOrigin = useOptionalRequestInfo()?.origin
	if (currentOrigin && origin === currentOrigin) return path
	return `${origin}${path === '/' ? '' : path}`
}

/** Resolves a path that belongs to the recruiter application. */
export function useAppHref(path: string): string {
	return useSiteHref(useHostOrigins().app, path)
}

/** `true` when the given href leaves the current origin. */
export function isExternalHref(href: string): boolean {
	return /^[a-z][a-z0-9+.-]*:\/\//i.test(href)
}
